// Single source of truth for the status of a submission (Ergebnisse).
//
// Not to be confused with the applicant status in the Bewerbungspool: that one
// sits on `campaigns.applicants[]` and keeps its own, separate list.
//
// "In Kontakt" sits alongside "Interview" and "Probestunde" instead of being
// replaced by them: it covers the stretch where someone has been contacted but
// neither is scheduled yet, which comes up often enough that HR needs a word
// for it.
//
// `selectable` is how a status gets retired without rewriting the rows that
// still carry it: the pickers keep showing it where it stands but refuse to set
// it. Nothing is retired at the moment.
export const SUBMISSION_STATUSES = [
    { value: 'Unbearbeitet', label: 'Unbearbeitet', order: 1, selectable: true },
    { value: 'In Kontakt', label: 'In Kontakt', order: 2, selectable: true },
    { value: 'Interview', label: 'Interview', order: 3, selectable: true },
    { value: 'Probestunde', label: 'Probestunde', order: 4, selectable: true },
    { value: 'Bewerbung zurückgezogen', label: 'Bewerbung zurückgezogen', order: 5, selectable: true },
    { value: 'Absage', label: 'Absage', order: 6, selectable: true },
    { value: 'Zusage', label: 'Zusage', order: 7, selectable: true }
];

export const DEFAULT_STATUS = 'Unbearbeitet';

// Keyed by status value, in list order — used for labels, sorting and filters.
export const STATUS_CONFIG = SUBMISSION_STATUSES.reduce((acc, { value, label, order, selectable }) => {
    acc[value] = { label, order, selectable };
    return acc;
}, {});

// A status that exists in old data but is no longer offered.
export const isLegacyStatus = (status) => STATUS_CONFIG[status]?.selectable === false;

// What a status picker may show: everything still settable, plus the current
// value when that is a legacy one. Without the latter a picker would hide
// where the submission stands, or silently overwrite it on the next change.
export const statusChoices = (current) =>
    SUBMISSION_STATUSES.filter((status) => status.selectable || status.value === current);

// The statuses that mean someone has been contacted — the "Kontaktiert" stage
// of the Bewerbungsfluss, which every one of them has passed through.
export const CONTACTED_STATUSES = SUBMISSION_STATUSES
    .filter(({ value }) => value !== 'Unbearbeitet' && value !== 'Bewerbung zurückgezogen')
    .map(({ value }) => value);

const STATUS_COLORS = {
    'Unbearbeitet': 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700',
    'In Kontakt': 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
    'Interview': 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700',
    'Probestunde': 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700',
    'Bewerbung zurückgezogen': 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700',
    'Absage': 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
    'Zusage': 'bg-green-100 text-green-800 border-green-400 dark:bg-green-900/30 dark:text-green-300 dark:border-green-400'
};

const FALLBACK_COLOR = 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-700';

// Case-insensitive so a status that reaches us oddly cased still gets its color.
const COLORS_BY_LOWER = Object.entries(STATUS_COLORS).reduce((acc, [value, classes]) => {
    acc[value.toLowerCase()] = classes;
    return acc;
}, {});

export const getStatusColor = (status) => COLORS_BY_LOWER[String(status ?? '').toLowerCase()] || FALLBACK_COLOR;
